import { useCallback, useMemo, useState } from "react";
import { formatUnits, parseUnits } from "viem";
import { useAccount, useBalance, useChainId, useSwitchChain } from "wagmi";
import { PRIMARY_CHAIN_ID } from "@/constants/chain";
import {
  AQUABERA_ADDRESS,
  AQUABERA_HENLO_DEPLOYER,
  AQUABERA_HENLO_VAULT,
  REWARD_VAULT_AQUABERA_ADDRESS,
} from "@/constants/contracts";
import { useLogin } from "@/hooks/auth";
import { useBlockRefetch } from "@/hooks/use-block-refetch";
import { useTransactionToast } from "@/hooks/use-transaction-toast";
import {
  useReadErc20Allowance,
  useReadErc20BalanceOf,
  useWriteAquaberaForwardDepositToIchiVault,
  useWriteAquaberaForwardNativeDepositToIchiVault,
  useWriteAquaberaForwardNativeWithdrawFromIchiVault,
  useWriteErc20Approve,
  useWriteRewardVaultStake,
} from "@/lib/onchain/generated";
import { useWriteWithToast } from "../use-write-with-toast";

// Contract addresses
const WBERA_ADDRESS = "0x6969696969696969696969696969696969696969";
const LP_TOKEN_ADDRESS = "0x04fD6a7B02E2e48caedaD7135420604de5f834f8";

export type TokenType = "bera" | "wbera";
export type VaultTab = "deposit" | "withdraw";

export function useVault() {
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { login } = useLogin();
  const depositToast = useTransactionToast({
    title: "Deposit liquidity into vault",
  });
  const withdrawToast = useTransactionToast({
    title: "Withdraw liquidity from vault",
  });
  const approveToast = useTransactionToast({ title: "Approve LP tokens" });
  const wberaApproveToast = useTransactionToast({ title: "Approve WBERA" });
  const _approveStakeToast = useTransactionToast({
    title: "Approve LP for staking",
  });
  const _stakeToast = useTransactionToast({ title: "Stake LP tokens" });

  // Transaction helpers (Radix toasts + receipt tracking)
  const depositTx = useWriteWithToast({
    title: "Deposit liquidity into vault",
    onSuccess: () => handleDepositSuccess(),
  });
  const withdrawTx = useWriteWithToast({
    title: "Withdraw liquidity from vault",
    onSuccess: () => handleWithdrawSuccess(),
  });
  const approveTx = useWriteWithToast({
    title: "Approve LP tokens",
    onSuccess: () => handleApproveSuccess(),
  });
  const wberaApproveTx = useWriteWithToast({
    title: "Approve WBERA",
    onSuccess: () => handleWberaApproveSuccess(),
  });
  const stakeTx = useWriteWithToast({
    title: "Stake LP tokens",
    onSuccess: async () => {
      await refetchLpBalance();
      setPendingStakeAmount(null);
    },
  });
  const stakeApproveTx = useWriteWithToast({
    title: "Approve LP for staking",
    onSuccess: async () => {
      if (pendingStakeAmount && pendingStakeAmount > 0n) {
        await stakeTx.run(() =>
          writeStake({
            address: REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
            args: [pendingStakeAmount],
          }),
        );
      }
    },
  });

  // State
  const [amount, setAmount] = useState<string>("");
  const [selectedToken, setSelectedToken] = useState<TokenType>("bera");
  const [autoStake, setAutoStake] = useState<boolean>(true); // Auto-stake by default
  const [activeTab, setActiveTab] = useState<VaultTab>(() => {
    if (typeof window !== "undefined") {
      const savedTab = localStorage.getItem("vaultActiveTab");
      return savedTab === "deposit" || savedTab === "withdraw" ? savedTab : "deposit";
    }
    return "deposit";
  });

  // Balances
  const { data: beraBalance, refetch: refetchBeraBalance } = useBalance({
    address,
    chainId: PRIMARY_CHAIN_ID,
    query: { enabled: !!address, refetchOnWindowFocus: true, staleTime: 0 },
  });

  const { data: wberaBalance, refetch: refetchWberaBalance } = useReadErc20BalanceOf({
    address: WBERA_ADDRESS,
    args: [address ?? "0x0"],
    query: { enabled: !!address, refetchOnWindowFocus: true, staleTime: 0 },
    chainId: PRIMARY_CHAIN_ID,
  });

  const { data: lpBalance, refetch: refetchLpBalance } = useReadErc20BalanceOf({
    address: LP_TOKEN_ADDRESS,
    args: [address ?? "0x0"],
    query: { enabled: !!address, refetchOnWindowFocus: true, staleTime: 0 },
    chainId: PRIMARY_CHAIN_ID,
  });

  // Allowances
  const { data: lpAllowance, refetch: refetchLpAllowance } = useReadErc20Allowance({
    address: LP_TOKEN_ADDRESS,
    args: [address ?? "0x0", AQUABERA_ADDRESS],
    query: { enabled: !!address, refetchOnWindowFocus: true, staleTime: 0 },
    chainId: PRIMARY_CHAIN_ID,
  });

  const { data: wberaAllowance, refetch: refetchWberaAllowance } = useReadErc20Allowance({
    address: WBERA_ADDRESS,
    args: [address ?? "0x0", AQUABERA_ADDRESS],
    query: { enabled: !!address, refetchOnWindowFocus: true, staleTime: 0 },
    chainId: PRIMARY_CHAIN_ID,
  });

  // LP Allowance for Reward Vault (for staking)
  const { data: lpAllowanceForRewardVault, refetch: refetchLpAllowanceForRewardVault } =
    useReadErc20Allowance({
      address: LP_TOKEN_ADDRESS,
      args: [address ?? "0x0", REWARD_VAULT_AQUABERA_ADDRESS],
      query: { enabled: !!address, refetchOnWindowFocus: true, staleTime: 0 },
      chainId: PRIMARY_CHAIN_ID,
    });

  // Keep key reads fresh via block cadence to avoid stale UI after actions
  useBlockRefetch({
    chainId: PRIMARY_CHAIN_ID,
    every: 2,
    enabled: !!address,
    refetch: [refetchBeraBalance, refetchWberaBalance, refetchLpBalance],
  });
  // Allowances do not need per-block updates; refresh at a lower cadence
  useBlockRefetch({
    chainId: PRIMARY_CHAIN_ID,
    every: 6,
    enabled: !!address,
    refetch: [refetchLpAllowance, refetchWberaAllowance, refetchLpAllowanceForRewardVault],
  });

  // Contract write hooks
  const { data: depositData, writeContractAsync: writeNativeDeposit } =
    useWriteAquaberaForwardNativeDepositToIchiVault();
  const { data: tokenDepositData, writeContractAsync: writeTokenDeposit } =
    useWriteAquaberaForwardDepositToIchiVault();
  const { data: withdrawData, writeContractAsync: writeNativeWithdraw } =
    useWriteAquaberaForwardNativeWithdrawFromIchiVault();
  const { data: approveData, writeContractAsync: writeApprove } = useWriteErc20Approve();
  const { data: wberaApproveData, writeContractAsync: writeWberaApprove } = useWriteErc20Approve();
  const { data: stakeData, writeContractAsync: writeStake } = useWriteRewardVaultStake();
  const { data: stakeApproveData, writeContractAsync: writeStakeApprove } = useWriteErc20Approve();

  // Internal state for auto-stake flow
  const [pendingStakeAmount, setPendingStakeAmount] = useState<bigint | null>(null);

  // Transaction monitoring with stable callbacks
  const handleDepositSuccess = useCallback(async () => {
    if (amount) {
      depositToast.onSuccess();

      // Refresh balances after deposit
      if (selectedToken === "bera") {
        await refetchBeraBalance();
      } else {
        await refetchWberaBalance();
        await refetchWberaAllowance();
      }

      // Wait for LP balance to update
      const updatedLpBalance = await refetchLpBalance();
      const newLpBalance = updatedLpBalance.data;

      // Auto-stake if enabled and we have LP tokens
      if (autoStake && newLpBalance && newLpBalance > 0n) {
        try {
          // Check LP token allowance for the reward vault (not Aquabera)
          const { data: currentLpAllowance } = await refetchLpAllowanceForRewardVault();

          if (!currentLpAllowance || currentLpAllowance < newLpBalance) {
            // Need to approve LP tokens for the reward vault
            setPendingStakeAmount(newLpBalance);
            await stakeApproveTx.run(() =>
              writeStakeApprove({
                address: LP_TOKEN_ADDRESS,
                args: [REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`, newLpBalance],
              }),
            );
          }
          // If already approved, stake immediately
          if (currentLpAllowance && currentLpAllowance >= newLpBalance) {
            await stakeTx.run(() =>
              writeStake({
                address: REWARD_VAULT_AQUABERA_ADDRESS as `0x${string}`,
                args: [newLpBalance],
              }),
            );
          }
        } catch (_error) {
          // Don't show error toast, just log it
        }
      }

      setAmount("");
    }
  }, [
    amount,
    selectedToken,
    depositToast,
    refetchBeraBalance,
    refetchWberaBalance,
    refetchLpBalance,
    refetchWberaAllowance,
    autoStake,
    writeStakeApprove,
    writeStake,
    refetchLpAllowanceForRewardVault,
    stakeApproveTx.run,
    stakeTx.run,
  ]);

  const handleWithdrawSuccess = useCallback(() => {
    if (amount) {
      withdrawToast.onSuccess();
      refetchBeraBalance();
      refetchLpBalance();
      refetchLpAllowance();
      setAmount("");
    }
  }, [amount, withdrawToast, refetchBeraBalance, refetchLpBalance, refetchLpAllowance]);

  const handleApproveSuccess = useCallback(() => {
    approveToast.onSuccess();
    refetchLpAllowance();
  }, [approveToast, refetchLpAllowance]);

  const handleWberaApproveSuccess = useCallback(() => {
    wberaApproveToast.onSuccess();
    refetchWberaAllowance();
  }, [wberaApproveToast, refetchWberaAllowance]);

  // Loading states from helpers
  const isDepositing = depositTx.isLoading;
  const isWithdrawing = withdrawTx.isLoading;
  const isApprovalLoading = approveTx.isLoading;
  const isWberaApprovalLoading = wberaApproveTx.isLoading;
  const isStakeApprovalLoading = stakeApproveTx.isLoading;
  const isStakingLoading = stakeTx.isLoading;

  // Computed values with stable dependencies
  const isOnBerachain = useMemo(() => chainId === PRIMARY_CHAIN_ID, [chainId]);

  const isTransactionInProgress = useMemo(
    () =>
      isDepositing ||
      isWithdrawing ||
      isApprovalLoading ||
      isWberaApprovalLoading ||
      isStakeApprovalLoading ||
      isStakingLoading,
    [
      isDepositing,
      isWithdrawing,
      isApprovalLoading,
      isWberaApprovalLoading,
      isStakeApprovalLoading,
      isStakingLoading,
    ],
  );

  const hasSufficientFunds = useMemo(() => {
    if (!amount) return true;
    try {
      const parsedAmount = parseUnits(amount, 18);
      if (activeTab === "deposit") {
        const currentBalance = selectedToken === "bera" ? beraBalance?.value : wberaBalance;
        return currentBalance ? currentBalance >= parsedAmount : false;
      } else {
        return lpBalance ? lpBalance >= parsedAmount : false;
      }
    } catch (_err) {
      return false;
    }
  }, [amount, selectedToken, beraBalance?.value, wberaBalance, lpBalance, activeTab]);

  const needsApproval = useMemo(() => {
    if (activeTab === "withdraw") {
      if (!amount || !lpAllowance) return true;
      try {
        const parsedAmount = parseUnits(amount, 18);
        return lpAllowance < parsedAmount;
      } catch (_err) {
        return true;
      }
    } else if (activeTab === "deposit" && selectedToken === "wbera") {
      if (!amount || !wberaAllowance) return true;
      try {
        const parsedAmount = parseUnits(amount, 18);
        return wberaAllowance < parsedAmount;
      } catch (_err) {
        return true;
      }
    }
    return false;
  }, [amount, lpAllowance, wberaAllowance, activeTab, selectedToken]);

  // Handlers with stable dependencies
  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isTransactionInProgress) {
        setAmount(e.target.value);
      }
    },
    [isTransactionInProgress],
  );

  const handleMaxAmount = useCallback(
    (percentage: number) => {
      if (isTransactionInProgress) return;

      if (activeTab === "deposit") {
        const currentBalance = selectedToken === "bera" ? beraBalance?.value : wberaBalance;
        if (currentBalance) {
          const maxAmount = formatUnits(currentBalance, 18);
          const adjustedPercentage = percentage === 1 ? 0.999 : percentage;
          const percentageAmount = (parseFloat(maxAmount) * adjustedPercentage).toString();
          setAmount(percentageAmount);
        }
      } else if (lpBalance) {
        const maxAmount = formatUnits(lpBalance, 18);
        const adjustedPercentage = percentage === 1 ? 0.999 : percentage;
        const percentageAmount = (parseFloat(maxAmount) * adjustedPercentage).toString();
        setAmount(percentageAmount);
      }
    },
    [
      activeTab,
      selectedToken,
      beraBalance?.value,
      wberaBalance,
      lpBalance,
      isTransactionInProgress,
    ],
  );

  const handleDeposit = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    if (!hasSufficientFunds) {
      return;
    }

    try {
      const parsedAmount = parseUnits(amount, 18);
      // Execute the deposit transaction and track toast
      depositToast.initiate();
      if (selectedToken === "bera") {
        const hash = await writeNativeDeposit({
          args: [AQUABERA_HENLO_VAULT, AQUABERA_HENLO_DEPLOYER, parsedAmount, address!],
          value: parsedAmount,
        });
        if (hash) depositToast.trackTransaction(hash);
      } else {
        const hash = await writeTokenDeposit({
          args: [
            AQUABERA_HENLO_VAULT,
            AQUABERA_HENLO_DEPLOYER,
            WBERA_ADDRESS,
            parsedAmount,
            parsedAmount,
            address!,
          ],
        });
        if (hash) depositToast.trackTransaction(hash);
      }
    } catch (error: any) {
      depositToast.onException(error);
      return;
    }
  }, [
    amount,
    hasSufficientFunds,
    selectedToken,
    address,
    writeNativeDeposit,
    writeTokenDeposit,
    depositToast,
  ]);

  const handleWithdraw = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    if (!hasSufficientFunds) {
      return;
    }

    if (needsApproval) {
      return;
    }

    try {
      const parsedAmount = parseUnits(amount, 18);
      withdrawToast.initiate();
      const hash = await writeNativeWithdraw({
        args: [AQUABERA_HENLO_VAULT, AQUABERA_HENLO_DEPLOYER, parsedAmount, address!, 0n, 0n],
      });
      if (hash) withdrawToast.trackTransaction(hash);
    } catch (_error: any) {
      // Reset amount on error
      setAmount("");
      return;
    }
  }, [amount, hasSufficientFunds, needsApproval, address, writeNativeWithdraw, withdrawToast]);

  const handleApprove = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    try {
      const parsedAmount = parseUnits(amount, 18);
      approveToast.initiate();
      const hash = await writeApprove({
        address: LP_TOKEN_ADDRESS,
        args: [AQUABERA_ADDRESS, parsedAmount],
      });
      if (hash) approveToast.trackTransaction(hash);
    } catch (error: any) {
      // Reset amount on error
      setAmount("");
      approveToast.onException(error);
      return;
    }
  }, [
    amount,
    writeApprove,
    approveToast.initiate,
    approveToast.onException,
    approveToast.trackTransaction,
  ]);

  const handleWberaApprove = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    try {
      const parsedAmount = parseUnits(amount, 18);
      wberaApproveToast.initiate();
      const hash = await writeWberaApprove({
        address: WBERA_ADDRESS,
        args: [AQUABERA_ADDRESS, parsedAmount],
      });
      if (hash) wberaApproveToast.trackTransaction(hash);
    } catch (error: any) {
      // Reset amount on error
      setAmount("");
      wberaApproveToast.onException(error);
      return;
    }
  }, [
    amount,
    writeWberaApprove,
    wberaApproveToast.initiate,
    wberaApproveToast.onException,
    wberaApproveToast.trackTransaction,
  ]);

  const handleSwitchNetwork = useCallback(() => {
    if (switchChain) {
      switchChain({ chainId: PRIMARY_CHAIN_ID });
    }
  }, [switchChain]);

  return {
    // State
    amount,
    selectedToken,
    activeTab,
    autoStake,
    setAmount,
    setSelectedToken,
    setActiveTab,
    setAutoStake,

    // Computed values
    isOnBerachain,
    isTransactionInProgress,
    hasSufficientFunds,
    needsApproval,

    // Balances
    beraBalance,
    wberaBalance,
    lpBalance,

    // Loading states
    isDepositing,
    isWithdrawing,
    isApprovalLoading,
    isWberaApprovalLoading,

    // Handlers
    handleAmountChange,
    handleMaxAmount,
    handleDeposit,
    handleWithdraw,
    handleApprove,
    handleWberaApprove,
    handleSwitchNetwork,
    login,
    address,

    // Additional functions for manual staking
    writeApprove,
    writeStake,
    refetchLpBalance,
    refetchLpAllowanceForRewardVault,
  };
}
